import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  Tooltip,
} from "@mui/material"
import VisibilityIcon from "@mui/icons-material/Visibility"
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ErrorIcon from "@mui/icons-material/Error"
import SearchIcon from "@mui/icons-material/Search"
import { validateVAT } from "../../helpers/aedevat"

// Color options for invoice buttons
const COLOR_OPTIONS = [
  { value: "#3b82f6", label: "Blue" },
  { value: "#16a34a", label: "Green" },
  { value: "#dc2626", label: "Red" },
  { value: "#d97706", label: "Orange" },
  { value: "#8b5cf6", label: "Purple" },
  { value: "#0891b2", label: "Cyan" },
  { value: "#64748b", label: "Slate" },
]

// Refcode options
const REFCODE_OPTIONS = [
  { value: "71.00.00", label: "71.00.00 (No VAT)" },
  { value: "71.00.01", label: "71.00.01 (With VAT)" },
  { value: "24.00.00", label: "24.00.00" },
  { value: "24.00.01", label: "24.00.01" },
  { value: "64.00.00", label: "64.00.00" },
  { value: "80.00.00", label: "80.00.00" },
]

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  )
}

const InvoiceCompanyDialog = ({
  open,
  onClose,
  company,
  onSave,
  saving,
  onTestAade,
  onTestEpsilon,
  testing,
  testResult,
}) => {
  const { t } = useTranslation()
  const isEditing = !!company

  const [activeTab, setActiveTab] = useState(0)
  const [showPassword, setShowPassword] = useState({
    etimologiera_api_key: false,
    epsilon_api_key: false,
    epsilon_password: false,
  })
  const [vatValidating, setVatValidating] = useState(false)
  const [vatValidationResult, setVatValidationResult] = useState(null)

  const [form, setForm] = useState({
    // Basic
    name: "",
    short_name: "",
    vat_number: "",
    tax_office: "",
    // Address
    address: "",
    city: "",
    postal_code: "",
    country: "Greece",
    phone: "",
    email: "",
    // Tax
    refcode: "71.00.01",
    applies_vat: true,
    vat_rate: 24.0,
    // e-timologiera
    etimologiera_enabled: false,
    etimologiera_api_key: "",
    etimologiera_company_id: "",
    // Epsilon
    epsilon_enabled: false,
    epsilon_url: "",
    epsilon_api_key: "",
    epsilon_username: "",
    epsilon_password: "",
    // EU
    eu_enabled: false,
    eu_vat_exemption_code: "7",
    eu_refcode: "",
    // Display
    button_color: "#3b82f6",
    sort_order: 0,
    is_active: true,
    is_default: false,
  })

  // Populate form when editing
  useEffect(() => {
    if (company) {
      setForm({
        name: company.name || "",
        short_name: company.short_name || "",
        vat_number: company.vat_number || "",
        tax_office: company.tax_office || "",
        address: company.address || "",
        city: company.city || "",
        postal_code: company.postal_code || "",
        country: company.country || "Greece",
        phone: company.phone || "",
        email: company.email || "",
        refcode: company.refcode || "71.00.01",
        applies_vat: company.applies_vat !== false,
        vat_rate: company.vat_rate ?? 24.0,
        etimologiera_enabled: company.etimologiera_enabled || false,
        etimologiera_api_key: company.etimologiera_api_key || "",
        etimologiera_company_id: company.etimologiera_company_id || "",
        epsilon_enabled: company.epsilon_enabled || false,
        epsilon_url: company.epsilon_url || "",
        epsilon_api_key: company.epsilon_api_key || "",
        epsilon_username: company.epsilon_username || "",
        epsilon_password: company.epsilon_password || "",
        eu_enabled: company.eu_enabled || false,
        eu_vat_exemption_code: company.eu_vat_exemption_code || "7",
        eu_refcode: company.eu_refcode || "",
        button_color: company.button_color || "#3b82f6",
        sort_order: company.sort_order ?? 0,
        is_active: company.is_active !== false,
        is_default: company.is_default || false,
      })
      setActiveTab(0)
      setVatValidationResult(null)
    } else {
      // Reset for create
      setForm({
        name: "",
        short_name: "",
        vat_number: "",
        tax_office: "",
        address: "",
        city: "",
        postal_code: "",
        country: "Greece",
        phone: "",
        email: "",
        refcode: "71.00.01",
        applies_vat: true,
        vat_rate: 24.0,
        etimologiera_enabled: false,
        etimologiera_api_key: "",
        etimologiera_company_id: "",
        epsilon_enabled: false,
        epsilon_url: "",
        epsilon_api_key: "",
        epsilon_username: "",
        epsilon_password: "",
        eu_enabled: false,
        eu_vat_exemption_code: "7",
        eu_refcode: "",
        button_color: "#3b82f6",
        sort_order: 0,
        is_active: true,
        is_default: false,
      })
      setActiveTab(0)
      setVatValidationResult(null)
    }
  }, [company, open])

  const handleChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value
    setForm((prev) => ({ ...prev, [field]: value }))

    // Auto-update applies_vat based on refcode
    if (field === "refcode") {
      const noVatRefcodes = ["71.00.00", "24.00.00", "64.00.00", "80.00.00"]
      setForm((prev) => ({
        ...prev,
        [field]: value,
        applies_vat: !noVatRefcodes.includes(value),
        vat_rate: noVatRefcodes.includes(value) ? 0 : 24.0,
      }))
    }
  }

  const handleSwitchChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.checked }))
  }

  // Validate VAT number with AADE and auto-fill company info
  const handleValidateVat = async () => {
    if (!form.vat_number.trim()) {
      setVatValidationResult({ success: false, message: t("Please enter a VAT number") })
      return
    }

    setVatValidating(true)
    setVatValidationResult(null)

    try {
      const result = await validateVAT(form.vat_number.trim())

      if (result && result.valid !== false) {
        // Auto-fill company info from AADE response
        setForm((prev) => ({
          ...prev,
          name: result.onomasia || result.commerTitle || prev.name,
          tax_office: result.doyDescr || result.doy || prev.tax_office,
          address: result.postalAddress || prev.address,
          city: result.postalAreaDescription || prev.city,
          postal_code: result.postalZipCode || prev.postal_code,
        }))
        setVatValidationResult({
          success: true,
          message: t("VAT validated successfully. Company info auto-filled."),
        })
      } else {
        setVatValidationResult({
          success: false,
          message: result.error || t("VAT validation failed"),
        })
      }
    } catch (error) {
      setVatValidationResult({
        success: false,
        message: error.message || t("Error validating VAT"),
      })
    } finally {
      setVatValidating(false)
    }
  }

  const handleSave = () => {
    // Validate required fields
    if (!form.name.trim() || !form.vat_number.trim() || !form.refcode.trim()) {
      return
    }
    onSave(form)
  }

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }))
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0", pb: 2 }}>
        {isEditing ? t("Edit Invoice Company") : t("Add Invoice Company")}
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={t("Basic")} />
            <Tab label={t("Tax")} />
            <Tab label={t("e-timologiera")} />
            <Tab label={t("Epsilon")} />
            <Tab label={t("EU")} />
            <Tab label={t("Display")} />
          </Tabs>
        </Box>

        <Box sx={{ px: 3, py: 2 }}>
          {/* Basic Tab */}
          <TabPanel value={activeTab} index={0}>
            <Stack spacing={2.5}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label={t("Company Name")}
                  value={form.name}
                  onChange={handleChange("name")}
                  fullWidth
                  required
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
                <TextField
                  label={t("Short Name (Button Label)")}
                  value={form.short_name}
                  onChange={handleChange("short_name")}
                  fullWidth
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  helperText={t("Displayed on invoice button")}
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label={t("VAT Number (AFM)")}
                  value={form.vat_number}
                  onChange={handleChange("vat_number")}
                  fullWidth
                  required
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={t("Validate VAT and auto-fill company info")}>
                          <IconButton
                            onClick={handleValidateVat}
                            disabled={vatValidating || !form.vat_number.trim()}
                            edge="end"
                            size="small"
                            sx={{ color: "#3b82f6" }}
                          >
                            {vatValidating ? (
                              <CircularProgress size={18} />
                            ) : (
                              <SearchIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label={t("Tax Office (DOY)")}
                  value={form.tax_office}
                  onChange={handleChange("tax_office")}
                  fullWidth
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Stack>

              {vatValidationResult && (
                <Alert
                  severity={vatValidationResult.success ? "success" : "error"}
                  sx={{ borderRadius: 2 }}
                  icon={vatValidationResult.success ? <CheckCircleIcon /> : <ErrorIcon />}
                  onClose={() => setVatValidationResult(null)}
                >
                  {vatValidationResult.message}
                </Alert>
              )}

              <TextField
                label={t("Address")}
                value={form.address}
                onChange={handleChange("address")}
                fullWidth
                multiline
                rows={2}
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label={t("City")}
                  value={form.city}
                  onChange={handleChange("city")}
                  fullWidth
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
                <TextField
                  label={t("Postal Code")}
                  value={form.postal_code}
                  onChange={handleChange("postal_code")}
                  fullWidth
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
                <TextField
                  label={t("Country")}
                  value={form.country}
                  onChange={handleChange("country")}
                  fullWidth
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label={t("Phone")}
                  value={form.phone}
                  onChange={handleChange("phone")}
                  fullWidth
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
                <TextField
                  label={t("Email")}
                  value={form.email}
                  onChange={handleChange("email")}
                  fullWidth
                  size="small"
                  type="email"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Stack>
            </Stack>
          </TabPanel>

          {/* Tax Tab */}
          <TabPanel value={activeTab} index={1}>
            <Stack spacing={2.5}>
              <TextField
                label={t("Reference Code (Refcode)")}
                value={form.refcode}
                onChange={handleChange("refcode")}
                select
                fullWidth
                required
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                SelectProps={{ native: true }}
              >
                {REFCODE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </TextField>

              <Stack direction="row" spacing={4} alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.applies_vat}
                      onChange={handleSwitchChange("applies_vat")}
                    />
                  }
                  label={t("Applies VAT")}
                />

                <TextField
                  label={t("VAT Rate (%)")}
                  value={form.vat_rate}
                  onChange={handleChange("vat_rate")}
                  type="number"
                  size="small"
                  sx={{ width: 120, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  disabled={!form.applies_vat}
                  inputProps={{ step: 0.5, min: 0, max: 100 }}
                />
              </Stack>

              <Alert severity="info" sx={{ borderRadius: 2 }}>
                {form.applies_vat
                  ? t("VAT will be applied to invoices using this company.")
                  : t("No VAT will be applied. VAT amounts will be set to zero.")}
              </Alert>
            </Stack>
          </TabPanel>

          {/* e-timologiera Tab (AADE/MyData) */}
          <TabPanel value={activeTab} index={2}>
            <Stack spacing={2.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.etimologiera_enabled}
                    onChange={handleSwitchChange("etimologiera_enabled")}
                  />
                }
                label={t("Enable e-timologiera.gr (AADE/MyData)")}
              />

              {form.etimologiera_enabled && (
                <>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    {t("e-timologiera.gr is a certified AADE provider for electronic invoicing and MyDATA transmission.")}
                  </Alert>

                  <TextField
                    label={t("API Key")}
                    value={form.etimologiera_api_key}
                    onChange={handleChange("etimologiera_api_key")}
                    fullWidth
                    size="small"
                    type={showPassword.etimologiera_api_key ? "text" : "password"}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility("etimologiera_api_key")}
                            edge="end"
                            size="small"
                          >
                            {showPassword.etimologiera_api_key ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label={t("Company ID")}
                    value={form.etimologiera_company_id}
                    onChange={handleChange("etimologiera_company_id")}
                    fullWidth
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    helperText={t("Your company ID in e-timologiera.gr system")}
                  />

                  {isEditing && (
                    <Box>
                      <Button
                        variant="outlined"
                        onClick={() => onTestAade(company.id)}
                        disabled={testing}
                        sx={{ borderRadius: 2 }}
                      >
                        {testing ? <CircularProgress size={20} /> : t("Test Connection")}
                      </Button>
                      {testResult?.type === "aade" && (
                        <Alert
                          severity={testResult.success ? "success" : "error"}
                          sx={{ mt: 2, borderRadius: 2 }}
                          icon={testResult.success ? <CheckCircleIcon /> : <ErrorIcon />}
                        >
                          {testResult.message}
                        </Alert>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </TabPanel>

          {/* Epsilon Tab */}
          <TabPanel value={activeTab} index={3}>
            <Stack spacing={2.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.epsilon_enabled}
                    onChange={handleSwitchChange("epsilon_enabled")}
                  />
                }
                label={t("Enable Epsilon Integration")}
              />

              {form.epsilon_enabled && (
                <>
                  <TextField
                    label={t("Epsilon API URL")}
                    value={form.epsilon_url}
                    onChange={handleChange("epsilon_url")}
                    fullWidth
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    placeholder="https://epsilonsmart13.epsilonnet.gr"
                  />

                  <TextField
                    label={t("Username")}
                    value={form.epsilon_username}
                    onChange={handleChange("epsilon_username")}
                    fullWidth
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />

                  <TextField
                    label={t("Password")}
                    value={form.epsilon_password}
                    onChange={handleChange("epsilon_password")}
                    fullWidth
                    size="small"
                    type={showPassword.epsilon_password ? "text" : "password"}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility("epsilon_password")}
                            edge="end"
                            size="small"
                          >
                            {showPassword.epsilon_password ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label={t("API Key (Optional)")}
                    value={form.epsilon_api_key}
                    onChange={handleChange("epsilon_api_key")}
                    fullWidth
                    size="small"
                    type={showPassword.epsilon_api_key ? "text" : "password"}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility("epsilon_api_key")}
                            edge="end"
                            size="small"
                          >
                            {showPassword.epsilon_api_key ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {isEditing && (
                    <Box>
                      <Button
                        variant="outlined"
                        onClick={() => onTestEpsilon(company.id)}
                        disabled={testing}
                        sx={{ borderRadius: 2 }}
                      >
                        {testing ? <CircularProgress size={20} /> : t("Test Connection")}
                      </Button>
                      {testResult?.type === "epsilon" && (
                        <Alert
                          severity={testResult.success ? "success" : "error"}
                          sx={{ mt: 2, borderRadius: 2 }}
                          icon={testResult.success ? <CheckCircleIcon /> : <ErrorIcon />}
                        >
                          {testResult.message}
                        </Alert>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </TabPanel>

          {/* EU Tab */}
          <TabPanel value={activeTab} index={4}>
            <Stack spacing={2.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.eu_enabled}
                    onChange={handleSwitchChange("eu_enabled")}
                  />
                }
                label={t("Enable EU Intra-Community Transactions")}
              />

              {form.eu_enabled && (
                <>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    {t("When invoicing EU customers (outside Greece), VAT exemption will be automatically applied.")}
                  </Alert>

                  <TextField
                    label={t("VAT Exemption Code")}
                    value={form.eu_vat_exemption_code}
                    onChange={handleChange("eu_vat_exemption_code")}
                    fullWidth
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    helperText={t("Code 7 for intra-community transactions")}
                  />

                  <TextField
                    label={t("EU Refcode (Optional)")}
                    value={form.eu_refcode}
                    onChange={handleChange("eu_refcode")}
                    fullWidth
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    helperText={t("Alternative refcode for EU transactions. Leave empty to use main refcode.")}
                  />
                </>
              )}
            </Stack>
          </TabPanel>

          {/* Display Tab */}
          <TabPanel value={activeTab} index={5}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t("Button Color")}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {COLOR_OPTIONS.map((color) => (
                    <Box
                      key={color.value}
                      onClick={() => setForm((prev) => ({ ...prev, button_color: color.value }))}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: color.value,
                        cursor: "pointer",
                        border:
                          form.button_color === color.value
                            ? "3px solid #1e293b"
                            : "3px solid transparent",
                        "&:hover": {
                          opacity: 0.8,
                        },
                      }}
                      title={color.label}
                    />
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t("Button Preview")}
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: form.button_color,
                    "&:hover": { bgcolor: form.button_color, opacity: 0.9 },
                    borderRadius: 2,
                    px: 3,
                  }}
                >
                  {form.short_name || form.name || t("Invoice")}
                </Button>
              </Box>

              <TextField
                label={t("Sort Order")}
                value={form.sort_order}
                onChange={handleChange("sort_order")}
                type="number"
                size="small"
                sx={{ width: 150, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                inputProps={{ min: 0 }}
                helperText={t("Lower numbers appear first")}
              />

              <Stack direction="row" spacing={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_active}
                      onChange={handleSwitchChange("is_active")}
                    />
                  }
                  label={t("Active")}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_default}
                      onChange={handleSwitchChange("is_default")}
                    />
                  }
                  label={t("Default Company")}
                />
              </Stack>
            </Stack>
          </TabPanel>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0" }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>
          {t("Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !form.name.trim() || !form.vat_number.trim() || !form.refcode.trim()}
          sx={{ borderRadius: 2 }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : t("Save")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default InvoiceCompanyDialog
